import { useState, useEffect, useCallback, useRef } from 'react';
import { onSnapshot, Timestamp, collection, query, where, orderBy, getDocs, limit, startAfter, DocumentSnapshot } from 'firebase/firestore';
import { db } from '../../../auth/firebaseConfig';
import { conversationService, CONVERSATIONS_PAGE_SIZE } from '../api/conversationService';
import { Conversation } from '../types';

// Page size for guest inquiries (consistent with conversations)
const GUEST_INQUIRIES_PAGE_SIZE = 15;

export const useConversations = (userId: string | undefined) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(false);

  // Store the last document for pagination
  const lastConversationDocRef = useRef<DocumentSnapshot | null>(null);
  const lastGuestDocRef = useRef<DocumentSnapshot | null>(null);
  
  // Store raw data for merging
  const rawConversationsRef = useRef<Conversation[]>([]);
  const rawGuestInquiriesRef = useRef<Conversation[]>([]);
  const conversationDocsRef = useRef<DocumentSnapshot[]>([]);
  const guestDocsRef = useRef<DocumentSnapshot[]>([]);

  // Hydrate participants helper
  const hydrateParticipants = async (all: Conversation[], currentUserId: string) => {
    const participantIdsToFetch = new Set<string>();
    all.forEach(c => {
      if (c.metadata?.type !== 'inquiry' && c.participantIds) {
        c.participantIds.forEach(pid => {
          if (pid !== currentUserId) participantIdsToFetch.add(pid);
        });
      }
    });

    if (participantIdsToFetch.size > 0) {
      try {
        const { userService } = await import('../api/userService');
        const userMap = await userService.getUsersByIds(Array.from(participantIdsToFetch));
        
        all.forEach(c => {
          if (c.metadata?.type !== 'inquiry' && c.participantIds) {
            c.participants = {};
            c.participantIds.forEach(pid => {
              if (userMap[pid]) {
                c.participants![pid] = userMap[pid];
              }
            });
          }
        });
      } catch (err) {
        console.error("Error hydrating users", err);
      }
    }
    return all;
  };

  useEffect(() => {
    if (!userId) {
      setConversations([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    
    // 1. Query standard conversations (paginated)
    const qConversations = conversationService.getUserConversationsPaginatedQuery(userId, CONVERSATIONS_PAGE_SIZE);
    
    // 2. Query guest inquiries (where user is recipient) - paginated
    const qGuestInquiries = query(
      collection(db, 'guestInquiries'),
      where('recipientId', '==', userId),
      orderBy('timestamp', 'desc'),
      limit(GUEST_INQUIRIES_PAGE_SIZE)
    );

    let unsubConversations: () => void;
    let unsubGuest: () => void;

    const mergeAndSet = async () => {
      const all = [...rawConversationsRef.current, ...rawGuestInquiriesRef.current].sort((a, b) => {
        const timeA = (a.lastMessage?.timestamp || a.updatedAt)?.toMillis() || 0;
        const timeB = (b.lastMessage?.timestamp || b.updatedAt)?.toMillis() || 0;
        return timeB - timeA;
      });

      const hydrated = await hydrateParticipants(all, userId);
      
      setConversations(hydrated);
      setLoading(false);
      
      // Determine if there might be more to load
      const conversationsHasMore = conversationDocsRef.current.length >= CONVERSATIONS_PAGE_SIZE;
      const guestHasMore = guestDocsRef.current.length >= GUEST_INQUIRIES_PAGE_SIZE;
      setHasMore(conversationsHasMore || guestHasMore);
    };

    unsubConversations = onSnapshot(qConversations, 
      (snapshot) => {
        conversationDocsRef.current = snapshot.docs;
        lastConversationDocRef.current = snapshot.docs.length > 0 
          ? snapshot.docs[snapshot.docs.length - 1] 
          : null;
          
        rawConversationsRef.current = snapshot.docs.map(doc => {
          const data = doc.data();
          const lastMessageTimestamp = data.lastMessageTimestamp as Timestamp;
          const legacyLastMessage = typeof data.lastMessage === 'string' ? data.lastMessage : null;

          return {
            ...data,
            id: doc.id,
            participantIds: data.participants,
            updatedAt: lastMessageTimestamp || data.updatedAt,
            createdAt: data.createdAt || lastMessageTimestamp,
            lastMessage: legacyLastMessage 
              ? { 
                  content: legacyLastMessage, 
                  senderId: 'unknown', 
                  timestamp: lastMessageTimestamp,
                  type: 'text',
                  readBy: []
                } 
              : (data.lastMessage ? {
                  ...data.lastMessage,
                  content: data.lastMessage.content || data.lastMessage.message || data.lastMessage.text || '',
                  senderId: data.lastMessage.senderId || 'unknown'
                } : null),
            unreadCount: (data.unreadCount && typeof data.unreadCount === 'object') ? (data.unreadCount[userId] || 0) : 0
          } as Conversation;
        });
        mergeAndSet();
      },
      (err) => { console.error("Error conversations:", err); setError(err); setLoading(false); }
    );

    unsubGuest = onSnapshot(qGuestInquiries,
      (snapshot) => {
        guestDocsRef.current = snapshot.docs;
        lastGuestDocRef.current = snapshot.docs.length > 0 
          ? snapshot.docs[snapshot.docs.length - 1] 
          : null;
          
        rawGuestInquiriesRef.current = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              participantIds: ['guest', userId],
              updatedAt: data.timestamp,
              createdAt: data.timestamp,
              lastMessage: {
                content: data.content,
                senderId: 'guest',
                timestamp: data.timestamp,
                type: 'text',
                readBy: []
              },
              metadata: {
                type: 'inquiry',
                propertyId: data.listingId,
                propertyName: data.propertyTitle,
                guestName: data.senderName,
                guestEmail: data.senderEmail
              },
              unreadCount: typeof data.unreadCount === 'number' ? data.unreadCount : (data.archived ? 0 : 1)
            } as any as Conversation; 
        });
        mergeAndSet();
      },
      (err) => { console.error("Error guest inquiries:", err); }
    );

    return () => {
      if (unsubConversations) unsubConversations();
      if (unsubGuest) unsubGuest();
    };
  }, [userId]);

  /**
   * Load more conversations (for infinite scroll)
   */
  const loadMoreConversations = useCallback(async () => {
    if (!userId || loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    
    try {
      const loadPromises: Promise<void>[] = [];
      
      // Load more standard conversations
      if (lastConversationDocRef.current) {
        const qMoreConversations = conversationService.getUserConversationsPaginatedQuery(
          userId, 
          CONVERSATIONS_PAGE_SIZE, 
          lastConversationDocRef.current
        );
        
        loadPromises.push(
          getDocs(qMoreConversations).then(snapshot => {
            if (snapshot.docs.length > 0) {
              lastConversationDocRef.current = snapshot.docs[snapshot.docs.length - 1];
              conversationDocsRef.current = [...conversationDocsRef.current, ...snapshot.docs];
              
              const newConversations = snapshot.docs.map(doc => {
                const data = doc.data();
                const lastMessageTimestamp = data.lastMessageTimestamp as Timestamp;
                const legacyLastMessage = typeof data.lastMessage === 'string' ? data.lastMessage : null;

                return {
                  ...data,
                  id: doc.id,
                  participantIds: data.participants,
                  updatedAt: lastMessageTimestamp || data.updatedAt,
                  createdAt: data.createdAt || lastMessageTimestamp,
                  lastMessage: legacyLastMessage 
                    ? { 
                        content: legacyLastMessage, 
                        senderId: 'unknown', 
                        timestamp: lastMessageTimestamp,
                        type: 'text',
                        readBy: []
                      } 
                    : (data.lastMessage ? {
                        ...data.lastMessage,
                        content: data.lastMessage.content || data.lastMessage.message || data.lastMessage.text || '',
                        senderId: data.lastMessage.senderId || 'unknown'
                      } : null),
                  unreadCount: (data.unreadCount && typeof data.unreadCount === 'object') ? (data.unreadCount[userId] || 0) : 0
                } as Conversation;
              });
              
              rawConversationsRef.current = [...rawConversationsRef.current, ...newConversations];
            }
          })
        );
      }
      
      // Load more guest inquiries
      if (lastGuestDocRef.current) {
        const qMoreGuest = query(
          collection(db, 'guestInquiries'),
          where('recipientId', '==', userId),
          orderBy('timestamp', 'desc'),
          startAfter(lastGuestDocRef.current),
          limit(GUEST_INQUIRIES_PAGE_SIZE)
        );
        
        loadPromises.push(
          getDocs(qMoreGuest).then(snapshot => {
            if (snapshot.docs.length > 0) {
              lastGuestDocRef.current = snapshot.docs[snapshot.docs.length - 1];
              guestDocsRef.current = [...guestDocsRef.current, ...snapshot.docs];
              
              const newGuests = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  id: doc.id,
                  participantIds: ['guest', userId],
                  updatedAt: data.timestamp,
                  createdAt: data.timestamp,
                  lastMessage: {
                    content: data.content,
                    senderId: 'guest',
                    timestamp: data.timestamp,
                    type: 'text',
                    readBy: []
                  },
                  metadata: {
                    type: 'inquiry',
                    propertyId: data.listingId,
                    propertyName: data.propertyTitle,
                    guestName: data.senderName,
                    guestEmail: data.senderEmail
                  },
                  unreadCount: typeof data.unreadCount === 'number' ? data.unreadCount : (data.archived ? 0 : 1)
                } as any as Conversation;
              });
              
              rawGuestInquiriesRef.current = [...rawGuestInquiriesRef.current, ...newGuests];
            }
          })
        );
      }
      
      await Promise.all(loadPromises);
      
      // Merge and set all conversations
      const all = [...rawConversationsRef.current, ...rawGuestInquiriesRef.current].sort((a, b) => {
        const timeA = (a.lastMessage?.timestamp || a.updatedAt)?.toMillis() || 0;
        const timeB = (b.lastMessage?.timestamp || b.updatedAt)?.toMillis() || 0;
        return timeB - timeA;
      });
      
      const hydrated = await hydrateParticipants(all, userId);
      setConversations(hydrated);
      
      // Update hasMore
      const conversationsHasMore = conversationDocsRef.current.length >= CONVERSATIONS_PAGE_SIZE && 
        conversationDocsRef.current.length % CONVERSATIONS_PAGE_SIZE === 0;
      const guestHasMore = guestDocsRef.current.length >= GUEST_INQUIRIES_PAGE_SIZE &&
        guestDocsRef.current.length % GUEST_INQUIRIES_PAGE_SIZE === 0;
      setHasMore(conversationsHasMore || guestHasMore);
      
    } catch (err) {
      console.error("Error loading more conversations:", err);
    } finally {
      setLoadingMore(false);
    }
  }, [userId, loadingMore, hasMore]);

  return { 
    conversations, 
    loading, 
    loadingMore,
    error, 
    hasMore,
    loadMoreConversations 
  };
};
